import { useParams } from "react-router-dom";
import {
  fetchCompetitorReportDataConfigurations,
  fetchOrUpdateCompetitorSectionData,
  updateCompetitorReportSection,
} from "../../apiService";
import CreativeReportTitle from "@/screens/creatives/components/CreativeReportTitle";
import { useCompetitorReportStore } from "../../store";
import {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useRef,
} from "react";
import axios, { CancelTokenSource } from "axios";
import {
  Creative,
  CreativeReportAd,
  CreativeReportData,
  CreativeReportRow,
  CreativeReportSection,
  InsightType,
} from "@/screens/creatives/types";
import {
  CreativeSectionConfiguration,
  CreativeReportMetadata,
  ConfigurationAdditionalConfig,
} from "@/types";
import CreativeTable from "@/screens/creatives/components/table/CreativeTable";
import ReportLoader from "@/screens/creatives/components/ReportLoader";
import { Cell } from "@tanstack/react-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Portal } from "@radix-ui/react-tooltip";
import { Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IconCards, IconFilter } from "@tabler/icons-react";
import { capitalizeFirstLetter } from "@/screens/utils/Helpers";
import ImageSvg from "@/assets/image.svg";
import VideoSvg from "@/assets/video.svg";
import MixedSvg from "@/assets/mixed.svg";
import CreativeGalleryPreview from "@/screens/creatives/components/preview/CreativeGalleryPreview";
import { cn } from "@/lib/utils";
import APIErrorCard from "@/screens/creatives/components/table/APIErrorCard";
import { deleteLabelV2 } from "@/screens/creative-label/apiService";
import NoDataCard from "@/screens/creatives/components/NoDataCard";
import { useInsights } from "@/screens/creatives/hooks/useInsights";
import ReportInsights from "@/components/common/ReportInsights";
import PageLoader from "@/components/common/loaders/PageLoader";
import Filters from "@/components/common/filters/Filters";
import { SqButton } from "@/components/squircle-ui/button";
import CreativeReportDropdowns from "@/screens/creatives/components/CreativeReportDropdowns";
import { clearPlatformCache } from "@/screens/creatives/apiService";

const mediaSvgs = {
  mixed: MixedSvg,
  video: VideoSvg,
  image: ImageSvg,
  "N/A": MixedSvg,
  "": MixedSvg,
};

const HaveWeTriedThisCell = ({
  cell,
  handleOpenGallery,
}: {
  cell: Cell<CreativeReportRow, unknown>;
  handleOpenGallery: () => void;
}) => {
  const account_ads = cell.row.original.account_ads as CreativeReportAd[];
  const creativeType = cell.row.original.account_ad_creative_type as string;
  const [hasImageError, setHasImageError] = useState(false);
  const imageUrl =
    account_ads[0]?.creative?.thumbnail_url ||
    account_ads[0]?.creative.images[0]?.permalink_url;

  if (account_ads.length === 0) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger className="w-full">
            <div className="text-[14px] text-brand-primary-new hover:text-brand-primary-600 text-left">
              Not Tried
            </div>
          </TooltipTrigger>
          <Portal>
            <TooltipContent
              side="bottom"
              align="start"
              className="text-[12px] max-w-[340px] text-left z-[50] bg-black shadow-sm border-0"
              avoidCollisions
              alignOffset={25}
              collisionPadding={10}
            >
              <p className="whitespace-normal break-words text-[#fff]">Not Tried</p>
            </TooltipContent>
          </Portal>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="flex gap-3 items-center cursor-pointer">
      <div
        className="relative group"
        onClick={(e) => {
          e.stopPropagation();
          handleOpenGallery();
        }}
      >
        <div className="flex items-center bg-[#EDEFF3] justify-center min-w-16 w-16 h-16 overflow-hidden rounded-[8px]">
          {account_ads[0]?.creative?.thumbnail_url && !hasImageError ? (
            creativeType === "video" ? (
              <img
                src={account_ads[0]?.creative?.thumbnail_url}
                alt="Image"
                loading="lazy"
                className="w-full h-full object-cover"
                onError={() => setHasImageError(true)}
              />
            ) : (
              <img
                src={imageUrl}
                alt={"Image"}
                loading="lazy"
                className="w-full h-full object-cover"
                onError={() => setHasImageError(true)}
              />
            )
          ) : (
            <Image size={32} strokeWidth={1.5} className="text-[#B5BCCB]" />
          )}
        </div>
        <div
          className={`absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center rounded-[8px]`}
        >
          <IconCards size={24} className="text-white" />
        </div>
      </div>
      <div className="overflow-hidden flex-1 flex flex-col gap-2">
        <div className="w-full flex justify-between items-center gap-2">
          <div className="flex gap-2 items-center">
            {creativeType && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <img
                      src={mediaSvgs[creativeType as keyof typeof mediaSvgs]}
                      alt={creativeType}
                      className="shrink-0 h-9 w-9 rounded-[8px] cursor-default"
                    />
                  </TooltipTrigger>
                  <Portal>
                    <TooltipContent side="right" align="start" alignOffset={-10}>
                      <p className="text-[12px] text-white">
                        {capitalizeFirstLetter(creativeType)}
                        {creativeType !== "mixed" && "s"}
                      </p>
                    </TooltipContent>
                  </Portal>
                </Tooltip>
              </TooltipProvider>
            )}

            <Button
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenGallery();
              }}
              className="text-[12px] rounded-[8px] border border-border-gray bg-white flex items-center gap-[6px] py-[6px] px-[14px]"
            >
              <IconCards size={16} className="text-brand-primary-800" />
              <span className="overflow-hidden text-brand-primary-800 text-ellipsis text-[12px] font-normal leading-[19.157px] tracking-[0.24px]">
                {account_ads.length} Ads
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export interface ReportSectionProps {
  className: string;
  section: CreativeReportSection;
  handleSectionTitleUpdate: (
    sectionId: string,
    data: { title?: string; icon?: string },
  ) => Promise<void>;
  updateSectionConfigState: (
    reportId: string,
    sectionId: string,
    sectionConfig: CreativeSectionConfiguration,
  ) => void;
}

export interface ReportSectionRef {
  handleSectionDataUpdate: (
    reportMetadata?: CreativeReportMetadata,
    sectionConfig?: CreativeSectionConfiguration,
  ) => Promise<void>;
}

const ReportSection = forwardRef<ReportSectionRef, ReportSectionProps>(
  ({ className, section, handleSectionTitleUpdate, updateSectionConfigState }, ref) => {
    const handleTitleChange = (data: { title?: string; icon?: string }) =>
      void handleSectionTitleUpdate(section.id, data);

    const { accountId } = useParams();
    const {
      selectedReport,
      setSelectedReport,
      setIsAutoSaving,
      dataConfiguration,
      setDataConfiguration,
    } = useCompetitorReportStore();

    const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
    const [sectionData, setSectionData] = useState<CreativeReportData | null>(null);
    const [openGallery, setOpenGallery] = useState<boolean>(false);
    const [galleryMedia, setGalleryMedia] = useState<
      (Creative & {
        type: "image" | "video" | "mixed";
        ad_name: string;
        ad_id: string;
        creative_id: string;
      })[]
    >([]);
    const isMounted = useRef(true);
    const cancelTokenSourceRef = useRef<CancelTokenSource | null>(null);
    const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const getSectionData = async (
      reportMetadata?: CreativeReportMetadata,
      sectionConfig?: CreativeSectionConfiguration,
    ) => {
      if (!accountId) return;

      // Cancel any previous requests
      if (cancelTokenSourceRef.current) {
        cancelTokenSourceRef.current.cancel("Operation canceled due to new request");
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }

      // Create new CancelToken for this request
      cancelTokenSourceRef.current = axios.CancelToken.source();
      setIsDataLoading(true);

      const pollData = async () => {
        if (!isMounted.current) return;
        try {
          const res = await fetchOrUpdateCompetitorSectionData({
            accountId: accountId,
            sectionId: section.id,
            reportMetadata: reportMetadata || selectedReport?.metadata,
            sectionConfiguration: sectionConfig || section.configuration,
            cancelToken: cancelTokenSourceRef.current?.token,
          });

          // Check if this is still the latest request
          if (!isMounted.current) return;

          setSectionData(res);
          if (res.status === "pending" || res.status === "partial") {
            pollingTimeoutRef.current = setTimeout(() => {
              if (isMounted.current) {
                void pollData();
              }
            }, 2000); // Poll every 2 seconds
          }
          if (res.status === "failed") {
            setIsDataLoading(false);
          }
          if (res.status === "success") setIsDataLoading(false);
        } catch (error: unknown) {
          // Ignore canceled request errors
          if (axios.isCancel(error)) return;
          if (!isMounted.current) return;
          console.error("Error fetching section data:", error);
          setSectionData(null);
          setSectionData({
            status: "failed",
            error_type: "retry",
            message: "Failed to load competitor data. Please try again.",
            message_title: "Failed to load data",
          });
          setIsDataLoading(false);
        }
      };

      try {
        await pollData();
      } catch (error: unknown) {
        // Handle any errors from pollData
        if (!axios.isCancel(error)) {
          console.error("Error in pollData:", error);
        }
      }
    };

    // Expose getSectionData to parent
    useImperativeHandle(ref, () => ({
      handleSectionDataUpdate: (
        reportMetadata?: CreativeReportMetadata,
        sectionConfig?: CreativeSectionConfiguration,
      ) => getSectionData(reportMetadata, sectionConfig),
    }));

    useEffect(() => {
      void getSectionData();
      return () => {
        isMounted.current = false;
        if (cancelTokenSourceRef.current) {
          cancelTokenSourceRef.current.cancel("Component unmounted");
        }
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const CustomCell = ({ cell }: { cell: Cell<CreativeReportRow, unknown> }) => {
      const handleOpenGallery = () => {
        const account_ads = cell.row.original.account_ads as CreativeReportAd[];
        if (account_ads.length > 0) {
          const galleryItems = account_ads.map((ad) => ({
            ...ad.creative,
            type: cell.row.original.creative_type,
            ad_name: ad.ad_name || "",
            creative_id: ad.creative_id || "",
            ad_id: ad.ad_id || "",
          }));
          setGalleryMedia(galleryItems);
          setOpenGallery(true);
        }
      };

      return <HaveWeTriedThisCell cell={cell} handleOpenGallery={handleOpenGallery} />;
    };

    const updateAdditionalConfig = (additionalConfig: ConfigurationAdditionalConfig) => {
      if (!selectedReport) return;
      const updatedReport = {
        ...selectedReport,
        sections:
          selectedReport?.sections.map((temp_section) =>
            temp_section.id === section.id
              ? {
                  ...section,
                  configuration: {
                    ...section.configuration,
                    ...sectionData?.section?.configuration,
                    additional_config: {
                      ...(section.configuration?.additional_config || {}),
                      ...additionalConfig,
                    },
                  },
                }
              : temp_section,
          ) ?? [],
      };
      //okay
      setSelectedReport(updatedReport);
      if (!accountId || !sectionData) return;
      setIsAutoSaving(true);
      updateCompetitorReportSection(accountId, selectedReport.id, section.id, {
        configuration: {
          ...section.configuration,
          ...sectionData.section?.configuration,
          additional_config: {
            ...(section.configuration?.additional_config || {}),
            ...additionalConfig,
          },
        },
      })
        .catch((error) => {
          console.error("Error in updating additional config:", error);
        })
        .finally(() => {
          setIsAutoSaving(false);
        });
    };

    const getAndSetDataConfigurations = useCallback(async () => {
      if (!accountId) return;
      try {
        const res = await fetchCompetitorReportDataConfigurations(accountId);
        setDataConfiguration(res.configuration);
      } catch (error) {
        console.error(error);
      }
    }, [accountId, setDataConfiguration]);
    const sectionInfo = dataConfiguration?.section_types["competitor"];

    // Insight handling
    const { insightsLoading, insights, generateInsights, setInsights } = useInsights();

    const generateInsight = (promptOrId: { prompt_id?: string; prompt?: string }) => {
      if (accountId && selectedReport?.id)
        generateInsights(accountId, selectedReport?.id, section.id, promptOrId).catch(
          (error) => {
            console.error("Error generating insights:", error);
          },
        );
    };

    return (
      <div className={cn("py-[16px]", className)} data-section-id={section.id}>
        <CreativeReportTitle
          data={{
            title: section.title,
            icon: section?.icon ?? "",
          }}
          setData={handleTitleChange}
          titlePlaceholder={section.title}
        />
        <div className="flex gap-2 items-center flex-wrap mt-[16px]">
          {selectedReport && dataConfiguration && (
            <CreativeReportDropdowns
              accountId={accountId ?? ""}
              sectionId={section.id}
              dropdowns={
                dataConfiguration?.dropdowns?.filter((d) =>
                  sectionInfo?.enabled_keys.includes(`dropdowns.${d.key}`),
                ) ?? []
              }
              dropdownsValues={section.configuration.dropdowns ?? {}}
              onChange={(updatedDropdowns) => {
                const updatedConfig: CreativeSectionConfiguration = {
                  ...section.configuration,
                  dropdowns: {
                    ...section.configuration.dropdowns,
                    ...updatedDropdowns,
                  },
                };
                updateSectionConfigState(selectedReport?.id, section.id, updatedConfig);
                void getSectionData(selectedReport.metadata, updatedConfig);
              }}
              handleLabelDelete={async (labelId: string) => {
                try {
                  if (accountId) {
                    await (
                      deleteLabelV2 as (
                        accountId: string,
                        labelId: string,
                      ) => Promise<unknown>
                    )(accountId, labelId);
                    await getAndSetDataConfigurations();
                    const sectionsToRemove = selectedReport?.sections
                      .filter(
                        (_section) =>
                          _section?.configuration?.dropdowns?.["group_by"] === labelId,
                      )
                      .map((item) => item.id);
                    if (selectedReport) {
                      setSelectedReport({
                        ...selectedReport,
                        sections: selectedReport.sections.filter(
                          (_section) => !sectionsToRemove?.includes(_section.id),
                        ),
                      });
                    }
                  }
                } catch (error) {
                  console.error(error);
                }
              }}
              onBrandAdded={(brand) => {
                setDataConfiguration({
                  ...dataConfiguration,
                  dropdowns: dataConfiguration?.dropdowns?.map((d) =>
                    d.key === "competitors"
                      ? {
                          ...d,
                          options: [
                            {
                              key: brand.id,
                              label: brand.name,
                              profile_image_url: brand.profile_image_url,
                            },
                            ...d.options,
                          ],
                        }
                      : d,
                  ),
                });
              }}
            />
          )}

          {dataConfiguration?.filter && sectionInfo?.enabled_keys.includes("filter") && (
            <Filters
              selectedFilters={section?.configuration?.filters ?? []}
              onFilterChange={(filters) => {
                if (filters) {
                  const updatedConfig: CreativeSectionConfiguration = {
                    ...section.configuration,
                    filters,
                  };
                  updateSectionConfigState(
                    selectedReport?.id ?? "",
                    section.id,
                    updatedConfig,
                  );
                  void getSectionData(selectedReport?.metadata, updatedConfig);
                }
              }}
              selectedDate={selectedReport?.metadata?.date ?? null}
              dataConfigFilter={dataConfiguration?.filter || {}}
              showAddFilters={true}
              customTrigger={
                <SqButton
                  variant="outline"
                  className="border-border-gray h-8"
                  shadow={"sm"}
                >
                  <div className="flex items-end gap-2">
                    <IconFilter className="h-4 w-4 text-brand-orange" />
                    <span className="text-brand-orange text-[12px] font-normal leading-none">
                      Add Filter
                    </span>
                  </div>
                </SqButton>
              }
              selectedFilterSize={32}
              className="contents"
              hideAdvancedFilters
            />
          )}
        </div>
        {isDataLoading && sectionData?.status === "pending" ? (
          <div className="my-[250px] w-full flex justify-center items-center">
            <ReportLoader
              showProgressBar
              message={
                sectionData?.status === "pending"
                  ? (sectionData?.message ?? "Just a moment...")
                  : "Just a moment..."
              }
            />
          </div>
        ) : isDataLoading || !sectionData ? (
          <div className="my-[250px] w-full flex justify-center items-center">
            <PageLoader />
          </div>
        ) : sectionData.status === "failed" ? (
          <APIErrorCard
            className="min-h-[550px] justify-center"
            onRetry={() => {
              const retry = async () => {
                if (accountId && sectionData.error_type === "retry") {
                  try {
                    await clearPlatformCache(accountId, {
                      ...selectedReport?.metadata,
                      ...section.configuration,
                    });
                  } catch (error) {
                    console.error("Error clearing platform cache during retry:", error);
                  }
                }

                await getSectionData();
              };
              void retry();
            }}
            message={
              sectionData?.message ?? "Failed to load competitor data. Please try again."
            }
            messageTitle={sectionData?.message_title ?? "Failed to load data"}
          />
        ) : !sectionData.data || sectionData.data?.rows.length === 0 ? (
          <div className="my-[180px] w-full h-1/2">
            <NoDataCard
              heading="No data found for this set of filters and date range!"
              message="Adjust the date range or change filters."
            />
          </div>
        ) : (
          <div className="mt-4">
            {sectionData && (
              <CreativeTable
                data={sectionData.data?.rows ?? []}
                columns={sectionData.data?.columns ?? []}
                viewMode="table_view"
                hideRightPanelTrigger
                disableGallerySearch={true}
                CustomCell={CustomCell}
                status={sectionData?.status}
                partialColumns={sectionData.data?.partial_columns}
                preservePaginationState={true}
                initialColumnSizing={
                  sectionData?.section?.configuration?.additional_config
                    ?.column_configuration
                }
                initialSortState={
                  sectionData?.section?.configuration?.additional_config?.sort_state
                }
                onColumnResize={(columnSizing: Record<string, number>) => {
                  if (updateAdditionalConfig)
                    updateAdditionalConfig({
                      column_configuration: columnSizing,
                    });
                }}
                onSort={(sortState) => {
                  if (updateAdditionalConfig)
                    updateAdditionalConfig({
                      sort_state: sortState,
                    });
                }}
                sectionId={section.id}
                accountId={accountId}
                disableLabelManagement
              />
            )}
            <ReportInsights
              insights={insights}
              setInsights={setInsights}
              type={InsightType.CompetitorInsights}
              loading={insightsLoading}
              accountId={accountId}
              generateInsight={generateInsight}
              initialInsight={section?.insight}
              initialPrompt={section?.insight_prompt}
              error={section?.configuration?.error ?? ""}
              collapsible
            />
          </div>
        )}
        <CreativeGalleryPreview
          open={openGallery}
          onOpenChange={setOpenGallery}
          creatives={galleryMedia}
          accountId={accountId || ""}
          disableLabelManagement
          enableSearch={false}
        />
      </div>
    );
  },
);

export default ReportSection;
